import { isAndroid } from './platform';
import { usePrefs } from './prefs';
import { play } from './sound';

/**
 * Haptics (R23): navigator.vibrate on Android only. iOS has no Vibration API and the switch
 * checkbox trick is off the table (reportedly patched in iOS 26.5), so on iOS and everywhere
 * else the score taps get a very quiet audio tick instead. The tick is a Web Audio blip on the
 * 'ambient' session, so the silent switch mutes it, and it needs both the Haptics and the
 * Sound setting on. Nothing here ever throws.
 */
export type HapticKind = 'tap' | 'plus1' | 'plus2' | 'plus3' | 'undo' | 'error' | 'win' | 'stamp';

export const HAPTIC_PATTERNS: Record<HapticKind, number[]> = {
  tap: [8],
  plus1: [12],
  plus2: [12, 40, 12],
  plus3: [12, 40, 12, 40, 12],
  undo: [30],
  error: [50, 40, 50],
  win: [20, 60, 20, 60, 90],
  stamp: [25],
};

/** The kinds that get the audio tick where there is no vibration (taps and undo stay silent). */
export const TICK_KINDS: ReadonlySet<HapticKind> = new Set(['plus1', 'plus2', 'plus3', 'stamp']);
export const TICK_VOLUME = 0.3;

export function canVibrate(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function' && isAndroid();
}

export function haptic(kind: HapticKind): void {
  const prefs = usePrefs.getState();
  if (!prefs.haptics) return;
  if (canVibrate()) {
    try {
      navigator.vibrate(HAPTIC_PATTERNS[kind]);
    } catch {
      // Some WebViews throw without a user gesture. Nothing to do.
    }
    return;
  }
  if (TICK_KINDS.has(kind)) void play('tick', TICK_VOLUME).catch(() => {});
}
